import React from "react";

import AutosuggestHighlightMatch from "autosuggest-highlight/match";
import AutosuggestHighlightParse from "autosuggest-highlight/parse";
import styled from "styled-components";
import { mediaQueryForCss } from "utilities/mediaQuery";
import { throttle } from "lodash-es";

// ------------------------------------------------------------------------

export const SuggestionItem = styled.div`
    width: 100%;
    text-align: left !important;
    position: relative;
    font-family: ${props => props.theme.fontFamily.openSans};

    .item-main {
        padding-left: 20px;
        font-size: 14px;
        color: ${props => props.theme.colours.blackTints800.hex};
        ${mediaQueryForCss({
            "font-size": ["", "", "12px"]
        })}
    }

    .item-sub {
        padding-left: 20px;
        font-size: 10px;
        font-weight: 600;
        color: ${props => props.theme.colours.blackTints400.hex};
    }

    .item-seperator {
        position: absolute;
        width: 100%;
        height: 1px;
        background-color: rgba(102, 102, 102, 0.08);
        bottom: -11px;
    }
`;

// ------------------------------------------------------------------------

const getGoogleSuggestionsSetup = (autoCompleteService, placesService, autoCompleteSessionToken) => {
    const handleGoogleScript = google => {
        if (google.maps.places && autoCompleteService.current === null) {
            autoCompleteService.current = google.maps.places.AutocompleteSuggestion;
            console.log("If: Places - ", google.maps.places, "Autocomplete Service - ", autoCompleteService);
            refreshSessionToken(google);
        } else {
            console.log("Error: Places - ", google.maps.places, "Autocomplete Service - ", autoCompleteService);
        }
    };

    const refreshSessionToken = google => {
        if (google && google.maps && google.maps.places) {
            autoCompleteSessionToken.current = new google.maps.places.AutocompleteSessionToken();
        } else {
            console.warn("Google Maps Places library not fully loaded when trying to refresh session token.");
            autoCompleteSessionToken.current = null; // Or a temporary placeholder
        }
    };

    // Keep throttle for predictions as before if needed
    const fetchPredictions = throttle((request, callback) => {
        if (autoCompleteService.current) {
            const { suggestions } = autoCompleteService.current.fetchAutocompleteSuggestions(request);
            callback(suggestions);
        } else {
            console.warn("AutocompleteService not initialized.");
            callback([]);
        }
    }, 200);

    // Modify getPostalCode to use the new google.maps.places.Place.fetchUsingDataFields (Promise-based)
    const getPostalCode = (google, placeId, callback) => {
        // Ensure placeId and the new Place class are available
        if (!placeId || !google || !google.maps || !google.maps.places || !google.maps.places.Place) {
            console.error("Google Maps Places API or Place class not available for getPostalCode");
            callback(null); // Call callback with null or error
            return;
        }

        // Use the new Promise-based fetchUsingDataFields
        google.maps.places.Place.fetchUsingDataFields({
            placeId: placeId,
            sessionToken: autoCompleteSessionToken.current, // Use the current session token
            fields: ["address_components"] // Specify *only* the fields you need
        })
            .then(place => {
                let postalCode = "";
                // The structure of address_components is similar
                if (place && place.address_components) {
                    place.address_components.forEach(obj => {
                        if (obj.types && obj.types.length) {
                            // Check if 'postal_code' is one of the types
                            const isPostalCode = obj.types.some(type => type === "postal_code");
                            if (isPostalCode) {
                                postalCode = obj.long_name;
                            }
                        }
                    });
                }
                callback(postalCode); // Call the original callback with the result
                refreshSessionToken(google); // Refresh the token after a successful details fetch
            })
            .catch(error => {
                console.error("Error fetching place details:", error);
                callback(null); // Call the original callback with null on error
                refreshSessionToken(google); // It's good practice to refresh the token on error too
            });
    };

    const handleOnSuggestionsFetch = (searchValue, setResultsCallback, country = "AU") => {
        if (!searchValue) {
            setResultsCallback([]);
            return;
        }
        if (!autoCompleteService.current) {
            console.error("AutocompleteService not initialized. Cannot fetch suggestions.");
            setResultsCallback([]);
            return;
        }
        fetchPredictions(
            {
                input: searchValue,
                componentRestrictions: { country: [country] },
                sessionToken: autoCompleteSessionToken.current
            },
            result => setResultsCallback(result ? (result.length ? result : []) : [])
        );
    };

    const handleGetSuggestionValue = suggestion => {
        return suggestion;
    };

    const handleRenderSuggestion = (suggestion, { query }) => {
        if (!suggestion || !suggestion.description) return null;

        const matches = AutosuggestHighlightMatch(suggestion.description, query);
        const parts = AutosuggestHighlightParse(suggestion.description, matches);

        return (
            <SuggestionItem data-testid="suggestion-item">
                <div className="item-main">
                    {parts.map((part, index) => {
                        // Add key index
                        return (
                            <span
                                key={`${part.text}-${index}`} // Use index or unique part identifier
                                style={{ fontWeight: part.highlight ? "700" : "400" }}
                            >
                                {part.text}
                            </span>
                        );
                    })}
                </div>
                <div className="item-seperator"></div>
            </SuggestionItem>
        );
    };

    return {
        getPostalCode,
        handleGoogleScript,
        handleOnSuggestionsFetch,
        handleGetSuggestionValue,
        handleRenderSuggestion
    };
};

export default getGoogleSuggestionsSetup;
